// Keep the form in place on errors; a missing response does not prove the account was not created.
export function initRegistrationForm() {
    const form = document.querySelector('[data-registration-form]');
    if (!form) return;

    const feedback = form.querySelector('[data-registration-feedback]');
    const help = form.querySelector('[data-registration-help]');
    const button = form.querySelector('[type="submit"]');
    let submitting = false;

    form.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (submitting || !form.reportValidity()) return;
        submitting = true;
        button.disabled = true;
        feedback.textContent = '';
        if (help) help.hidden = true;
        form.querySelectorAll('[data-registration-error]').forEach(el => {
            const input = form.elements.namedItem(el.dataset.registrationError);
            if (input instanceof HTMLElement) {
                const descriptions = (input.getAttribute('aria-describedby') || '').split(' ').filter(id => id && id !== el.id);
                if (descriptions.length) input.setAttribute('aria-describedby', descriptions.join(' '));
                else input.removeAttribute('aria-describedby');
            }
            el.remove();
        });
        form.querySelectorAll('[aria-invalid="true"]').forEach(el => el.removeAttribute('aria-invalid'));

        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 30000);
        let response;
        try {
            response = await fetch(form.action, {
                method: 'POST',
                body: new FormData(form),
                headers: {'Accept': 'application/json'},
                credentials: 'same-origin',
                signal: controller.signal,
            });
            // Proxies and expired sessions can return HTML rather than JSON.
            const data = await response.json().catch(() => null);
            if (response.ok && typeof data?.redirect === 'string') {
                const destination = new URL(data.redirect, window.location.origin);
                if (destination.origin === window.location.origin) {
                    window.location.assign(destination.href);
                    return;
                }
            }

            let fieldErrors = 0;
            if (response.status === 422 && data?.errors && typeof data.errors === 'object') {
                for (const [name, messages] of Object.entries(data.errors)) {
                    const message = (Array.isArray(messages) ? messages : [messages]).filter(item => typeof item === 'string').join(' ');
                    if (!message) continue;
                    const input = form.elements.namedItem(name);
                    const error = document.createElement('div');
                    error.className = 'text-danger small mt-1';
                    error.dataset.registrationError = name;
                    error.id = `registration-error-${++fieldErrors}`;
                    error.textContent = message;
                    if (input instanceof HTMLElement) {
                        input.setAttribute('aria-invalid', 'true');
                        input.setAttribute('aria-describedby', `${input.getAttribute('aria-describedby') || ''} ${error.id}`.trim());
                        (input.closest('.input-group, .form-select-container') || input).after(error);
                    } else {
                        feedback.append(error);
                    }
                }
            }
            if (fieldErrors) {
                // Preserve messages for validation fields absent from this form.
                feedback.prepend(document.createTextNode(form.dataset.validationMessage));
            } else {
                const messageKey = response.status === 419 ? 'sessionMessage'
                    : response.status === 429 ? 'rateLimitMessage'
                    : [401, 403].includes(response.status) ? 'accessMessage'
                    : [404, 405].includes(response.status) ? 'unavailableMessage'
                    : [408, 504].includes(response.status) || controller.signal.aborted ? 'timeoutMessage'
                    : response.status >= 500 ? 'serverMessage'
                    : 'failureMessage';
                feedback.textContent = form.dataset[messageKey];
                if (help) help.hidden = false;
            }
        } catch {
            feedback.textContent = controller.signal.aborted ? form.dataset.timeoutMessage
                : response ? form.dataset.failureMessage : form.dataset.networkMessage;
            if (help) help.hidden = false;
        } finally {
            clearTimeout(timeout);
            form.querySelectorAll('input[name="password"], input[name="password_confirmation"]').forEach(input => {
                input.value = '';
            });
            submitting = false;
            button.disabled = false;
        }
        const captcha = form.querySelector('[data-captcha-widget-id]');
        if (captcha && window.grecaptcha?.reset) {
            window.grecaptcha.reset(Number(captcha.dataset.captchaWidgetId));
        }
        feedback.focus();
    });
}
